import type { ApiErrorEnvelope } from '@myscaw/shared';

import { ApiError, NetworkError } from './ApiError';

/**
 * FND-7.4. The only place in the front end that calls fetch.
 *
 * Two properties it exists to guarantee:
 *
 *   `credentials: 'include'` on every request, because the session is an opaque
 *   HttpOnly cookie (RG150). There is no token to attach, nothing in
 *   localStorage and no Authorization header - by design, and the reason a
 *   forgotten option here logs everyone out rather than failing loudly.
 *
 *   Every non-2xx becomes an ApiError carrying the envelope, so the React Query
 *   handler of document 20 section 3.4 has a status and a messageKey to act on
 *   rather than a rejected promise of unknown shape.
 *
 * `api` is callable and also carries `get`/`post`/`patch`/`put`/`delete`/
 * `upload`. Both forms reached this file from different branches and both are
 * in use; they are one implementation with two spellings, not two clients. A
 * second fetch call site is what this file exists to prevent.
 */

/**
 * Empty in development: vite.config.ts proxies /api to the API so the browser
 * sees one origin and SameSite=Lax keeps the cookie. Set to the API's origin in
 * staging and production, where the two are genuinely different hosts and CORS
 * with `credentials: true` carries the cookie instead.
 */
const API_ORIGIN = import.meta.env.VITE_API_URL ?? '';

/**
 * `/api/v1`, not `/api` - uml/18-api-specification.md section 2, and the prefix
 * FND-3 sets with `app.setGlobalPrefix` in `apps/api/src/config/global-prefix.ts`.
 * A client pointed at `/api` 404s on every endpoint in the system.
 */
const PREFIX = '/api/v1';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** multipart/form-data uploads: the body is sent as-is, no JSON header. */
  formData?: FormData;
}

/**
 * D-040, LIB phase 7a. A full API URL for a plain `<a href>`. A download is a
 * browser navigation that follows the API's 302 to a signed link; it is not a
 * `fetch`, so it cannot go through `request`. Built here so the origin and the
 * `/api/v1` prefix still live in exactly one file.
 */
export function apiUrl(path: string): string {
  return `${API_ORIGIN}${PREFIX}${path}`;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, formData } = options;

  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      method,
      credentials: 'include',
      signal,
      headers: formData ? undefined : { 'Content-Type': 'application/json' },
      body: formData ?? (body === undefined ? undefined : JSON.stringify(body)),
    });
  } catch (cause) {
    // fetch rejects only when the request never completed: offline, DNS, a
    // cancelled navigation. An HTTP error is a resolved promise.
    throw new NetworkError(cause);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const payload = await readJson(response);

  if (!response.ok) {
    throw new ApiError(response.status, envelopeOf(payload), retryAfterOf(response));
  }

  return payload as T;
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    // A proxy or a gateway answering HTML on a 502. There is no envelope to
    // find; the status is all the information there is.
    return null;
  }
}

/** Seconds from `Retry-After`, read by the global error handler's 429 toast. */
function retryAfterOf(response: Response): number | null {
  // `headers` is optional-chained: a test double, or a polyfilled fetch, may
  // omit it, and a missing header must never turn an ApiError into a TypeError.
  const seconds = Number.parseInt(response.headers?.get('retry-after') ?? '', 10);
  return Number.isNaN(seconds) || seconds <= 0 ? null : seconds;
}

function envelopeOf(payload: unknown): ApiErrorEnvelope['error'] | null {
  if (payload !== null && typeof payload === 'object' && 'error' in payload) {
    return (payload as ApiErrorEnvelope).error;
  }
  return null;
}

interface ApiClient {
  <T>(path: string, options?: RequestOptions): Promise<T>;
  get: <T>(path: string, signal?: AbortSignal) => Promise<T>;
  post: <T>(path: string, body?: unknown) => Promise<T>;
  patch: <T>(path: string, body?: unknown) => Promise<T>;
  put: <T>(path: string, body?: unknown) => Promise<T>;
  delete: <T>(path: string, body?: unknown) => Promise<T>;
  /** uml/18 section 2.7: one field named `file`. */
  upload: <T>(path: string, file: File, payload?: unknown) => Promise<T>;
}

/**
 * Re-exported because the screens merged from `origin/omar` import `ApiError`
 * from this module rather than from `./ApiError`. One class, two import paths -
 * not a second error type. `instanceof` still works from either.
 */
export { ApiError, NetworkError } from './ApiError';

export const api: ApiClient = Object.assign(request, {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', body }),
  upload: <T>(path: string, file: File, payload?: unknown) => {
    const formData = new FormData();
    formData.append('file', file);
    if (payload !== undefined) formData.append('payload', JSON.stringify(payload));
    return request<T>(path, { method: 'POST', formData });
  },
});
